import { Button } from '@monorepo/shared/componentsV2/Button';
import { Description } from '@monorepo/shared/componentsV2/Description';
import { BaseModal } from '@monorepo/shared/componentsV2/modals/BaseModal';
import { ModalFormInstructions } from '@monorepo/shared/componentsV2/modals/contents/ModalFormInstructions';
import { Select } from '@monorepo/shared/componentsV2/Select/Select';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { DocumentFolderWithFileCountResponse } from 'mapistry-shared';
import {
  getDocumentFolderMoveDestinations,
  isOrganizationScopedFolder,
} from '@monorepo/shared/utils/documentFolderScope';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDeleteDocumentFolder } from '../../hooks/useDeleteDocumentFolder';

interface DeleteFolderModalProps {
  organizationId: string;
  folder: DocumentFolderWithFileCountResponse;
  documentFolders: DocumentFolderWithFileCountResponse[];
  onClose: () => void;
}

const Bold = styled.span`
  font-weight: 700;
`;

const HelperDescription = styled(Description)`
  margin-bottom: 1rem;
`;

export function DeleteFolderModal({
  organizationId,
  folder,
  documentFolders,
  onClose,
}: DeleteFolderModalProps) {
  const { showUserFriendlyErrorToast, success } = useToast();
  const [destinationId, setDestinationId] = useState<string>('');

  const [deleteFolder, { status }] = useDeleteDocumentFolder({
    config: { throwOnError: true },
  });

  const moveDestinations = useMemo(
    () => getDocumentFolderMoveDestinations(documentFolders, folder),
    [documentFolders, folder],
  );

  const options = useMemo(
    () =>
      moveDestinations.map((destination) => ({
        value: destination.id,
        label: destination.name,
      })),
    [moveDestinations],
  );

  const hasFiles = folder.fileCount > 0;
  const requiresDestination = hasFiles;
  const isDestinationAvailable = options.length > 0;
  const canConfirm =
    !requiresDestination ||
    (isDestinationAvailable && destinationId.length > 0);

  const handleConfirm = useCallback(async () => {
    try {
      await deleteFolder({
        organizationId,
        folderId: folder.id,
        moveFilesToFolderId: requiresDestination ? destinationId : null,
      });
      success(`Folder "${folder.name}" was deleted.`);
      onClose();
    } catch (err) {
      showUserFriendlyErrorToast(
        err,
        `Unable to delete folder "${folder.name}".`,
      );
    }
  }, [
    deleteFolder,
    destinationId,
    folder,
    onClose,
    organizationId,
    requiresDestination,
    showUserFriendlyErrorToast,
    success,
  ]);

  const isDeleting = status === 'loading';

  return (
    <BaseModal
      open
      onClose={isDeleting ? undefined : onClose}
      showCloseButton
      title="Delete folder"
      maxWidth="sm"
      buttons={[
        <Button
          key="cancel"
          variant="text"
          color="primary"
          onClick={onClose}
          disabled={isDeleting}
        >
          Cancel
        </Button>,
        <Button
          key="confirm"
          variant="contained"
          color="secondary"
          onClick={handleConfirm}
          disabled={!canConfirm || isDeleting}
        >
          {isDeleting ? 'Deleting...' : 'Delete folder'}
        </Button>,
      ]}
    >
      <ModalFormInstructions>
        Are you sure you want to delete <Bold>{folder.name}</Bold>?
      </ModalFormInstructions>
      {!hasFiles && (
        <Description>
          This folder is empty. Deleting it cannot be undone.
        </Description>
      )}
      {hasFiles && isDestinationAvailable && (
        <>
          <HelperDescription>
            This folder contains {folder.fileCount}{' '}
            {folder.fileCount === 1 ? 'file' : 'files'}. Choose a destination
            folder to move {folder.fileCount === 1 ? 'it' : 'them'} into before
            deleting. Files can only be moved to{' '}
            {isOrganizationScopedFolder(folder.scope)
              ? 'another all-sites folder'
              : 'another per-site folder'}
            .
          </HelperDescription>
          <Select
            name="destinationFolder"
            label={`Move files to ${
              isOrganizationScopedFolder(folder.scope) ? 'all-sites ' : ''
            }folder`}
            options={options}
            value={destinationId}
            onChange={(e) => setDestinationId(e.target.value as string)}
            required
          />
        </>
      )}
      {hasFiles && !isDestinationAvailable && (
        <Description>
          This folder contains files but there are no other{' '}
          {isOrganizationScopedFolder(folder.scope) ? 'all-sites' : 'per-site'}{' '}
          folders available to move them to. Create one first, then try again.
        </Description>
      )}
    </BaseModal>
  );
}
